<template>
	<div
		v-if="isDeletingGoal"
		class="tag-center__goal-delete-picker-container"
	>
		<GoalPicker
			:items="goalDeletePickerItems"
			:variant="isMobileViewport ? 'mobile' : 'default'"
			@confirm="handleGoalDeleteConfirm"
			@cancel="handleGoalDeleteCancel"
		/>
	</div>

	<div
		v-else
		ref="tagCenter"
		class="l-asport-one-column -left"
	>
		<!-- Active rules info -->
		<HighlightRulesInfo
			v-if="hasActiveHighlightRules"
			class="tag-center__highlight-rules-info"
			:highlight-rules="highlightRules"
			@close="onHighlightInfoClose"
		/>
		<!-- Header -->
		<div
			v-if="!isMobileViewport"
			:class="[!isNarrowTaggingTool ? 'l-asport-one-panel -no-flex-grow -enable-overflow' : '']"
		>
			<TagGroupsDropdown
				:options="tagGroupOptions"
				:value="selectedTagGroupOption"
				@input="onTagGroupSelectionChange"
				@create-tag-group="onCreateTagGroup"
			/>
		</div>
		<div
			v-scroll-container="{
				target: '.js-scroll-wrapper',
				disableScrollShadow: false,
			}"
			:class="[!isNarrowTaggingTool ? 'l-asport-one-panel -no-padding' : '']"
		>
			<div class="tag-center__content-inner-w">
				<!-- TagCenter Tabs -->
				<Tab
					class="tag-center__tabs"
					:tabs="tabs"
					:active-tab="activeTab"
					icon-size="lg"
					@tab-change="onTabChanged"
				>
					<template #tab-details-0>
						<div class="tag-center__tabs-scrollable-content js-scroll-wrapper">
							<NoEnabledSections
								v-if="!hasEnabledEventSections"
								class="l-asport-one-panel-h-padding"
							/>
							<MatchTagsSection
								v-if="enabledSections[SECTION_COMPETITION] && hasMatchTags"
								class="l-asport-one-panel-h-padding"
								:icon="visibleInPlayerIcon"
								:open="openSections[SECTION_COMPETITION]"
								:disabled="!allowedSections[SECTION_COMPETITION]"
								:disabled-hint="sectionDisabledHint"
								@toggle="toggleSection(SECTION_COMPETITION)"
								@click="handleMatchTagClick"
							/>
							<Accordion
								v-if="enabledSections[SECTION_SCOREBOARD] && isScoreboardSupported"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_SCOREBOARD]"
								:title="translateSectionLabel(SECTION_SCOREBOARD)"
								:icon="showScoreboardScore ? visibleInPlayerIcon : null"
								:icon-hint="transUcfirst('tag_group.visible_in_player')"
								:disabled="!allowedSections[SECTION_SCOREBOARD]"
								:disabled-hint="sectionDisabledHint"
								@toggle="toggleSection(SECTION_SCOREBOARD)"
							>
								<ScoreboardControl
									v-if="isScoreboardSupported"
									:event="event"
									:tags="eventTags"
									:position="playerPosition"
									:show-scoreboard-score-label="trans('one.show_scoreboard_in_player')"
									:show-scoreboard-time-label="trans('one.show_time_in_player')"
									:show-scoreboard-score="showScoreboardScore"
									:show-scoreboard-time="showScoreboardTime"
									:custom-text-label="trans('one.custom_text')"
									:custom-text="customText"
									:custom-text-enabled="customTextEnabled"
									:period-start-minutes="periodStartMinutes"
									@set-scoreboard-settings="handleSetScoreboardSettings"
									@set-period-start-minutes="handleSetPeriodStartMinutes"
									@score-increase="handleScoreIncrease"
									@score-decrease="handleScoreDecrease"
									@clock-start="handleClockStart"
									@clock-stop="handleClockStop"
									@clock-set="handleClockSet"
									@set-custom-text="handleSetCustomText"
									@set-custom-text-enabled="handleSetCustomTextEnabled"
								/>
							</Accordion>
							<CustomTagsSection
								v-if="enabledSections[SECTION_CUSTOM_EVENT] && customEventButtons.length > 0"
								class="l-asport-one-panel-h-padding"
								:tags="customEventButtons"
								:open="openSections[SECTION_CUSTOM_EVENT]"
								:title="translateSectionLabel(SECTION_CUSTOM_EVENT, isPlayerCompetition)"
								:disabled="!allowedSections[SECTION_CUSTOM_EVENT]"
								:disabled-hint="sectionDisabledHint"
								:info="customTagsSectionInfo"
								@toggle="toggleSection(SECTION_CUSTOM_EVENT)"
								@tag-click="handleCustomTagClick"
							/>
							<MarkInOutSection
								v-if="enabledSections[SECTION_MARKING_EVENT]"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_MARKING_EVENT]"
								:mark-in-enabled="isMarkInEnabled"
								:mark-out-enabled="isMarkOutEnabled"
								:disabled="!allowedSections[SECTION_MARKING_EVENT]"
								:disabled-hint="sectionDisabledHint"
								:icon="visibleInPlayerIcon"
								:icon-hint="transUcfirst('tag_group.visible_in_player')"
								@mark-in-click="handleMarkIn"
								@mark-out-click="handleMarkOut"
								@toggle="toggleSection(SECTION_MARKING_EVENT)"
							/>
							<CustomButtonsInfoPanel
								v-if="isEventInfoPanelEnabled"
								class="l-asport-one-panel-h-padding"
							/>
							<HighlightSection
								v-if="canUseHighlightIq && event.videoAvailable"
								class="l-asport-one-panel-h-padding"
							/>
						</div>
					</template>
					<template #tab-details-1>
						<div class="tag-center__tabs-scrollable-content js-scroll-wrapper">
							<NoEnabledSections
								v-if="!hasEnabledParticipantSections"
								class="l-asport-one-panel-h-padding"
							/>
							<div
								v-if="isSequentialCompetition"
								class="tag-center__athlete-selector-wrapper l-asport-one-panel-h-padding"
							>
								<AthleteSelector
									v-if="!isMobileViewport"
									:athletes="athletes"
									:value="selectedParticipant"
									@input="onAthleteSelect"
								/>
								<MobileAthleteSelector
									v-else
									:athletes="athletes"
									:value="selectedParticipant"
									@input="onAthleteSelect"
								/>
							</div>
							<AthleteRunSection
								v-if="isSequentialCompetition && enabledSections[SECTION_RUN]"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_RUN]"
								:mark-in-enabled="isMarkInEnabled"
								:mark-out-enabled="isMarkOutEnabled"
								:disabled="!allowedSections[SECTION_RUN]"
								:disabled-hint="sectionDisabledHint"
								:selected-athlete="selectedParticipant"
								@toggle="toggleSection(SECTION_RUN)"
							/>
							<ParticipantTagsSection
								v-if="enabledSections[SECTION_DEFAULT]"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_DEFAULT]"
								:title="translateSectionLabel(SECTION_DEFAULT, isPlayerCompetition)"
								:tags="defaultButtons"
								:participants="sectionParticipants"
								:icon="visibleInPlayerIcon"
								:disabled="!allowedSections[SECTION_DEFAULT]"
								:disabled-hint="sectionDisabledHint"
								@toggle="toggleSection(SECTION_DEFAULT)"
								@click="handleSportTagClick"
							/>
							<ParticipantTagsSection
								v-if="enabledSections[SECTION_ADDITIONAL] && additionalButtons.length > 0"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_ADDITIONAL]"
								:title="translateSectionLabel(SECTION_ADDITIONAL, isPlayerCompetition)"
								:tags="additionalButtons"
								:participants="sectionParticipants"
								:disabled="!allowedSections[SECTION_ADDITIONAL]"
								:disabled-hint="sectionDisabledHint"
								@toggle="toggleSection(SECTION_ADDITIONAL)"
								@click="handleSportTagClick"
							/>
							<ParticipantTagsSection
								v-if="enabledSections[SECTION_CUSTOM_PARTICIPANT] && customParticipantButtons.length > 0"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_CUSTOM_PARTICIPANT]"
								:title="translateSectionLabel(SECTION_CUSTOM_PARTICIPANT, isPlayerCompetition)"
								:tags="customParticipantButtons"
								:participants="sectionParticipants"
								:disabled="!allowedSections[SECTION_CUSTOM_PARTICIPANT]"
								:disabled-hint="sectionDisabledHint"
								:info="customTagsSectionInfo"
								@toggle="toggleSection(SECTION_CUSTOM_PARTICIPANT)"
								@click="handleSportTagClick"
							/>
							<AthleteMarkingSection
								v-if="enabledSections[SECTION_MARKING_ATHLETE]"
								class="l-asport-one-panel-h-padding"
								:open="openSections[SECTION_MARKING_ATHLETE]"
								:mark-in-enabled="isMarkInEnabled"
								:mark-out-enabled="isMarkOutEnabled"
								:disabled="!allowedSections[SECTION_MARKING_ATHLETE]"
								:disabled-hint="sectionDisabledHint"
								:selected-athlete="selectedParticipant"
								:show-athlete-selector="!isSequentialCompetition"
								@select-athlete="onAthleteSelect"
								@toggle="toggleSection(SECTION_MARKING_ATHLETE)"
							/>
							<CustomButtonsInfoPanel
								v-if="isParticipantInfoPanelEnabled"
								class="l-asport-one-panel-h-padding"
							/>
						</div>
					</template>
					<template #tab-details-2>
						<TemplateSection
							class="l-asport-one-panel-h-padding"
						/>
					</template>
				</Tab>
			</div>
		</div>
		<!-- disabled overlay -->
		<div
			v-if="isDisabled"
			class="tag-center__disable-overlay"
		>
			<p
				class="tag-center__toast"
				:class="toastClass"
			>
				{{ disabledMessage }}.
			</p>
		</div>
	</div>
</template>

<script>
import Accordion from '@components/accordion/Accordion';
import Tab from '@components/tab/Tab.vue';
import ParticipantTagsSection from '@videotools/js/one/components/TagCenter/sections/ParticipantTagsSection';
import MatchTagsSection from '@videotools/js/one/components/TagCenter/sections/MatchTagsSection';
import MarkInOutSection from '@videotools/js/one/components/TagCenter/sections/MarkInOutSection';
import AthleteRunSection from '@videotools/js/one/components/TagCenter/sections/AthleteRunSection';
import ScoreboardControl from '@videotools/js/one/components/TagCenter/ScoreboardControl';
import TemplateSection from '@videotools/js/one/components/TagCenter/sections/TemplateSection';
import GoalPicker from '@components/GoalPicker/GoalPicker';
import TagGroupsDropdown from '@videotools/js/one/components/TagCenter/TagGroupsDropdown';
import NoEnabledSections from '@videotools/js/one/components/TagCenter/NoEnabledSections';
import CustomButtonsInfoPanel from '@videotools/js/one/components/TagCenter/CustomButtonsInfoPanel';
import CustomTagsSection from '@videotools/js/one/components/TagCenter/sections/CustomTagsSection';
import AthleteMarkingSection from '@videotools/js/one/components/TagCenter/sections/AthleteMarkingSection';
import AthleteSelector from '@videotools/js/one/components/AthleteSelector/AthleteSelector';
import MobileAthleteSelector from '@videotools/js/one/components/AthleteSelector/MobileAthleteSelector';
import HighlightRulesInfo from '@videotools/js/one/components/TagCenter/HighlightRulesInfo';
import HighlightSection from '@videotools/js/one/components/highlights/HighlightSection';
import VisibilityIconMixin from '@videotools/js/mixins/visibility-icon';
import { bus, EVENT, playerBus } from '@videotools/js/events';
import {
	SECTION_ADDITIONAL,
	SECTION_COMPETITION,
	SECTION_CUSTOM_EVENT,
	SECTION_CUSTOM_PARTICIPANT,
	SECTION_DEFAULT,
	SECTION_MARKING_ATHLETE,
	SECTION_MARKING_EVENT,
	SECTION_RUN,
	SECTION_SCOREBOARD,
} from '@videotools/js/api/entities/TagGroup';
import SectionLabelsMixin from '@videotools/js/one/components/TagGroups/mixin/SectionLabelsMixin';
import * as SCORING_TAG from '@videotools/js/constants/scoring-tags';
import { events as HOTKEYS_EVENTS } from '@videotools/js/events/modules/player-hotkeys';
import { ATHLETE_UNKNOWN_KEY } from '@videotools/js/constants/athletes';
import { mapActions, mapGetters } from 'vuex';
import VScrollContainer from '@shared/directives/v-scroll-container';

const events = [
	EVENT.PLAYER.ON.PROGRESS,
	HOTKEYS_EVENTS.HOTKEY_IN_POINT,
	HOTKEYS_EVENTS.HOTKEY_OUT_POINT,
];
export default {
	name:       'TagCenter',
	components: {
		HighlightRulesInfo,
		AthleteMarkingSection,
		AthleteSelector,
		MobileAthleteSelector,
		CustomTagsSection,
		CustomButtonsInfoPanel,
		NoEnabledSections,
		TagGroupsDropdown,
		GoalPicker,
		ScoreboardControl,
		AthleteRunSection,
		MarkInOutSection,
		MatchTagsSection,
		ParticipantTagsSection,
		Tab,
		Accordion,
		TemplateSection,
		HighlightSection,
	},
	directives: {
		scrollContainer: VScrollContainer,
	},
	mixins: [VisibilityIconMixin, SectionLabelsMixin],
	data() {
		return {
			disableTicker:               null,
			isDisabled:                  false,
			scoreboardEnabled:           true,
			isMarking:                   false,
			markInTime:                  0,
			markInDuration:              0,
			selectedParticipant:         null,
			isDeletingGoal:              false,
			disabledMessage:             null,
			toastClass:                  'info',
			participantToDeleteGoalFrom: null,
			playerPosition:              0,
			SECTION_DEFAULT,
			SECTION_COMPETITION,
			SECTION_SCOREBOARD,
			SECTION_CUSTOM_EVENT,
			SECTION_CUSTOM_PARTICIPANT,
			SECTION_MARKING_EVENT,
			SECTION_MARKING_ATHLETE,
			SECTION_RUN,
			SECTION_ADDITIONAL,
		};
	},
	computed: {
		...mapGetters({
			isMobileViewport:            'device/isMobileViewport',
			isNarrowTaggingTool:         'device/isNarrowTaggingTool',
			sportName:                   'event/sportName',
			isPlayerCompetition:         'event/isPlayerCompetition',
			isConcurrentCompetition:     'event/isConcurrentCompetition',
			isSequentialCompetition:     'event/isSequentialCompetition',
			event:                       'event/event',
			eventTags:                   'event/tags',
			highlightRules:              'event/highlightRules',
			showScoreboardScore:         'event/showScoreboardScore',
			showScoreboardTime:          'event/showScoreboardTime',
			hasScoreboardSupportedSport: 'event/hasScoreboardSupportedSport',
			periodStartMinutes:          'event/periodStartMinutes',
			participants:                'event/participants',
			videoAvailable:              'event/videoAvailable',
			customText:                  'event/customText',
			customTextEnabled:           'event/customTextEnabled',
			athletes:                    'event/participants',
			tagTemplates:                'event/tagTemplates',
			isHighlightsEnabled:         'highlights/isHighlightsEnabled',
			isHighlightsProcessing:      'highlights/isProcessing',
			markingActive:               'markingZone/active',
			canViewTagCenter:            'permissions/canViewTagCenter',
			canUpdateTagTemplates:       'permissions/canUpdateTagTemplates',
			isGuest:                     'permissions/isGuest',
			canUseHighlightIq:           'permissions/canUseHighlightIq',
			cutTag:                      'tags/cutTag',
			runTag:                      'tags/runTag',
			_sportTags:                  'tags/participantTags',
			_customTags:                 'tags/customTags',
			_matchTags:                  'tags/matchTags',
			_defaultButtons:             'tagCenter/defaultButtons',
			_additionalButtons:          'tagCenter/additionalParticipantButtons',
			enabledSections:             'tagCenter/enabledSections',
			openSections:                'tagCenter/openSections',
			allowedSections:             'tagCenter/allowedSections',
			activeTab:                   'tagCenter/activeTab',
			tagGroups:                   'tagGroups/tagGroups',
			selectedTagGroup:            'tagGroups/selectedTagGroup',
			eventSections:               'tagGroups/eventSections',
			participantSections:         'tagGroups/participantSections',
			contextName:                 'user/contextName',
		}),
		isEventInfoPanelEnabled() {
			return this.isGuest && this.selectedTagGroup.isSystemDefault && !this.customEventButtons?.length;
		},
		isParticipantInfoPanelEnabled() {
			return this.isGuest && this.selectedTagGroup.isSystemDefault && !this.customParticipantButtons?.length;
		},
		tabs() {
			const tabs = [
				{
					key:   0,
					label: this.transChoiceUcFirst('commons.event', 1),
					icon:  'calendar',
				},
				{
					key:   1,
					label: this.isPlayerCompetition
						? this.transChoiceUcFirst('commons.athlete', 1)
						: this.transChoiceUcFirst('commons.team', 1),
					icon: 'users-wm',
				},
			];

			if (this.canUpdateTagTemplates && this.tagTemplates.length > 0) {
				tabs.push({
					key:   2,
					label: this.transChoiceUcFirst('one.template', 1),
					icon:  'numbered-list',
				});
			}

			return tabs;
		},
		labels() {
			return {
				title: this.transUcfirst('one.tag_center'),
			};
		},
		tagGroupOptions() {
			return this.tagGroups.map(tg => ({
				id:    tg.id,
				label: tg.name,
				value: tg,
				icon:  this.visibilityIcon(tg.visibility),
			}));
		},
		selectedTagGroupOption() {
			return this.tagGroupOptions.find(tg => tg.id === this.selectedTagGroup.id);
		},
		defaultButtons() {
			return this._defaultButtons.map(tag => ({
				key:       tag.id,
				label:     tag.name,
				value:     tag.id,
				type:      tag.type,
				highlight: tag.highlight,
				targets:   tag.targets,
			}));
		},
		additionalButtons() {
			return this._additionalButtons.map(tag => ({
				key:       tag.id,
				label:     tag.name,
				value:     tag.id,
				type:      tag.type,
				highlight: tag.highlight,
				targets:   tag.targets,
			}));
		},
		customParticipantButtons() {
			return this.selectedTagGroup.customParticipantButtons.map(tag => ({
				key:       tag.id,
				label:     tag.name,
				value:     tag.id,
				type:      tag.type,
				highlight: tag.highlight,
				targets:   tag.targets,
			}));
		},
		customEventButtons() {
			return this.selectedTagGroup.customEventButtons.map(tag => ({
				key:       tag.id,
				label:     tag.name,
				value:     tag.id,
				type:      tag.type,
				highlight: tag.highlight,
				targets:   tag.targets,
			}));
		},
		isMarkInEnabled() {
			return !this.isMarking;
		},
		isMarkOutEnabled() {
			return this.isMarking && !this.markOutDisabled;
		},
		markOutDisabled() {
			return this.markInDuration < 1;
		},
		isScoreboardSupported() {
			return this.hasScoreboardSupportedSport;
		},
		goalDeletePickerItems() {
			if (!this.participantToDeleteGoalFrom?.participatable?.id) return [];

			return this.eventTags
				.filter(
					(eventTag) =>
						eventTag.tag &&
						Object.values(SCORING_TAG).includes(eventTag.tag.name) &&
						eventTag.teamId === this.participantToDeleteGoalFrom.participatable.id
				)
				.map((eventTag) => {
					const tagName = this.transTagLabel(eventTag.tag?.name, this.event.sportName, eventTag.tag?.type);

					return {
						id:           eventTag.id,
						icon:         `tag-${eventTag.tag?.name}`,
						playerName:   eventTag.player?.name || '',
						playerNumber: eventTag.player?.number || '',
						team:         eventTag.team?.club?.code || '',
						time:         eventTag.formattedTimestamp,
						tagName,
					};
				});
		},
		sectionParticipants() {
			return this.isSequentialCompetition ? [this.selectedParticipant] : this.participants;
		},
		visibleInPlayerIcon() {
			return this.selectedTagGroup.isSystemDefault ? 'preview-outline' : null;
		},
		sectionDisabledHint() {
			return this.transUcfirst('one.permissions.view_only_message');
		},
		hasEnabledEventSections() {
			return this.eventSections.some(section => this.enabledSections[section]);
		},
		hasEnabledParticipantSections() {
			return this.participantSections.some(section => this.enabledSections[section]);
		},
		hasActiveHighlightRules() {
			return this.highlightRules?.length > 0;
		},
		customTagsSectionInfo() {
			if (this.isGuest) {
				return this.transUcfirst('one.tag_center_custom_tags_guest_info', { context: this.contextName });
			}
			return null;
		},
		hasMatchTags() {
			return this._matchTags && this._matchTags.length > 0;
		},
	},
	watch: {
		event() {
			this.init();
		},
	},
	created() {
		this.initStore();
		if (this.canUpdateTagTemplates) {
			this.getTagTemplates();
		}
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}

		this.resetTimelineActiveTags();
	},
	mounted() {
		this.init();
		playerBus.subscribePosition(this.handlePositionUpdate);
		const [firstTab] = this.tabs;
		const currentTab = this.tabs.find(tab => tab.key === this.activeTab) || firstTab;
		this.onTabChanged(currentTab.key);
	},
	destroyed() {
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
		playerBus.unsubscribePosition(this.handlePositionUpdate);
	},
	methods: {
		...mapActions({
			setSelectedTagGroup:       'tagGroups/setSelected',
			createEventTag:            'event/createEventTag',
			_toggleSection:            'tagCenter/toggleSection',
			initStore:                 'tagCenter/init',
			setScoreboardSettings:     'event/setScoreboardSettings',
			setPeriodStartMinutes:     'event/setPeriodStartMinutes',
			deleteEventTag:            'event/deleteEventTag',
			increaseScore:             'event/increaseScore',
			startClock:                'event/startClock',
			stopClock:                 'event/stopClock',
			setClock:                  'event/setClock',
			resetHighlightRules:       'event/resetHighlightRules',
			getTagTemplates:           'event/getTagTemplates',
			setActiveTab:              'tagCenter/setActiveTab',
			resetTimelineActiveTags:   'timelineTags/resetActiveTags',
			doStartMarking:            'markingZone/startMarking',
			doEndMarking:              'markingZone/endMarking',
			setCustomText:             'event/setCustomText',
			setCustomTextEnabled:      'event/setCustomTextEnabled',
			setShowTagGroupCreatePage: 'app/setShowTagGroupCreatePage',
		}),
		init() {
			if (!this.checkIfUpcoming()) {
				return;
			}
			this.checkPermissions();
		},
		checkPermissions() {
			if (!this.canViewTagCenter) {
				this.isDisabled = true;
				this.disabledMessage = this.trans('one.permissions.view_only_message');
				this.toastClass = 'info';
				return false;
			}
		},
		checkIfUpcoming() {
			if (this.event.liveInSeconds <= 0) return true;
			this.isDisabled = true;
			this.disabledMessage = this.trans('one.once_event_starts');
			this.toastClass = 'warning';

			clearTimeout(this.disableTicker);
			this.disableTicker = setTimeout(() => {
				this.isDisabled = false;
				this.disableTicker = null;
			}, this.event.liveInSeconds * 1000);

			return false;
		},
		onAthleteSelect(athlete) {
			this.selectedParticipant = athlete?.id === ATHLETE_UNKNOWN_KEY ? null : athlete;
		},
		onTagGroupSelectionChange(tagGroup) {
			this.setSelectedTagGroup(tagGroup);
		},
		onCreateTagGroup() {
			this.setShowTagGroupCreatePage(true);
		},
		onHighlightInfoClose() {
			this.resetHighlightRules();
		},
		async handleSportTagClick({ tag: tagOption, participant }) {
			const tagButton = this._sportTags.find(t => t.id === tagOption.value);
			if (!tagButton) {
				console.warn('Missing tag ', tagOption);
				return;
			}

			const timestamp = Math.round(playerBus.position);

			let participantId = null;
			if (participant) {
				participantId = participant.id;
			} else if (this.isPlayerCompetition && this.selectedParticipant && Number.isFinite(this.selectedParticipant.id)) {
				participantId = this.selectedParticipant.id;
			}

			// limit tag end to event duration
			const maxSecondsAfter = this.event.duration - timestamp;
			const secondsAfter = Math.min(tagButton.secondsAfter, maxSecondsAfter);

			await this.createEventTag({
				data: {
					name:            tagButton.name,
					eventId:         this.event.id,
					tagId:           tagButton.tag.id,
					participantId,
					timestamp,
					secondsBefore:   tagButton.secondsBefore,
					secondsAfter,
					timeline:        tagButton.timeline,
					taggingToolOnly: tagButton.taggingToolOnly,
					priority:        tagButton.priority,
					highlight:       tagButton.highlight,
				},
				backendSnap: this.$store.getters['device/isAppleDevice'],
			});

			bus.publish(EVENT.TIMELINE_TAG_CREATED, { time: timestamp, participantId, tag: tagButton });
		},
		handleMatchTagClick(tagOption) {
			const timestamp = Math.round(playerBus.position);
			const tagButton = this._matchTags.find(t => t.id === tagOption.value);

			if (!tagButton) {
				console.warn('Missing tag', tagOption);
				return;
			}

			this.createEventTag({
				data: {
					eventId:         this.event.id,
					tagId:           tagButton.tag.id,
					timestamp,
					secondsBefore:   tagButton.secondsBefore,
					secondsAfter:    tagButton.secondsAfter,
					priority:        tagButton.priority,
					timeline:        tagButton.timeline,
					taggingToolOnly: tagButton.taggingToolOnly,
					highlight:       tagButton.highlight,
				},
				backendSnap: this.$store.getters['device/isAppleDevice'],
			});

			bus.publish(EVENT.TIMELINE_TAG_CREATED, { time: timestamp, tag: tagButton });
		},
		async handleCustomTagClick(tagOption) {
			const timestamp = Math.round(playerBus.position);
			const tag = this._customTags.find(t => t.id === tagOption.value);
			await this.createEventTag({
				data: {
					eventId:         this.event.id,
					tagId:           tag.tag.id,
					timestamp,
					secondsBefore:   tag.secondsBefore,
					secondsAfter:    tag.secondsAfter,
					timeline:        tag.timeline,
					taggingToolOnly: tag.taggingToolOnly,
					priority:        tag.priority,
					highlight:       tag.highlight,
				},
				backendSnap: this.$store.getters['device/isAppleDevice'],
			});
		},
		startMarking() {
			if (this.markingActive) {
				return;
			}
			this.markInTime = playerBus.position;
			this.doStartMarking({ position: playerBus.position, tag: this.cutTag });
		},
		endMarking() {
			this.doEndMarking(playerBus.position);
			const duration = playerBus.position - this.markInTime;
			const timestamp = Math.round(playerBus.position - duration);
			this.markInTime = 0;
			this.isMarking = false;

			return { duration, timestamp };
		},
		handleMarkIn() {
			this.startMarking();
		},
		handleMarkOut() {
			const { duration, timestamp } = this.endMarking();
			if (!this.cutTag) {
				throw new Error('Unable to retrieve the `cut` tag from store.');
			}
			this.createCut(this.cutTag, timestamp, duration);
		},
		async createCut(eventTag, timestamp, duration, participantId = null) {
			await this.createEventTag({
				data: {
					eventId:         this.event.id,
					tagId:           eventTag.tag.id,
					participantId,
					timestamp,
					secondsBefore:   0,
					secondsAfter:    Math.round(duration),
					timeline:        eventTag.timeline,
					taggingToolOnly: eventTag.taggingToolOnly,
					priority:        eventTag.priority,
					highlight:       eventTag.highlight,
				},
				backendSnap: this.$store.getters['device/isAppleDevice'],
				absolute:    true,
			});
		},
		toggleSection(section) {
			this._toggleSection(section);
		},
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](elapsedSeconds) {
			this.markInDuration = this.markInTime ? Math.round(elapsedSeconds - this.markInTime) : 0;
		},
		handleSetScoreboardSettings({ showScore, showTime }) {
			this.setScoreboardSettings({ showScore, showTime });
		},
		handleSetPeriodStartMinutes(value) {
			this.setPeriodStartMinutes(value);
		},
		handleSetCustomText(value) {
			this.setCustomText(value);
		},
		handleSetCustomTextEnabled(value) {
			this.setCustomTextEnabled(value);
		},
		handleScoreIncrease({ participant, timestamp }) {
			this.increaseScore({ participant, timestamp });
		},
		handleScoreDecrease({ participant, _timestamp }) {
			this.participantToDeleteGoalFrom = participant;
			this.isDeletingGoal = true;
		},
		handleClockStart({ timestamp }) {
			this.startClock({ timestamp });
		},
		handleClockStop({ timestamp, value }) {
			this.stopClock({ timestamp, value });
		},
		handleClockSet({ timestamp, value }) {
			this.setClock({ timestamp, value });
		},
		handleDeleteTag(tag) {
			if (tag) {
				this.deleteEventTag(tag);
			}
		},
		handleGoalDeleteConfirm(item) {
			const eventTag = this.eventTags.find((eventTag) => eventTag.id === item.id);
			this.handleDeleteTag(eventTag);
			this.isDeletingGoal = false;
		},
		handleGoalDeleteCancel() {
			this.isDeletingGoal = false;
		},
		handlePositionUpdate(value) {
			this.playerPosition = value;
		},
		[`BUS-${HOTKEYS_EVENTS.HOTKEY_IN_POINT}`]() {
			this.handleMarkIn();
		},
		[`BUS-${HOTKEYS_EVENTS.HOTKEY_OUT_POINT}`]() {
			this.handleMarkOut();
		},
		onTabChanged(tab) {
			this.setActiveTab(tab);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.tag-center__highlight-rules-info {
	position: relative;
	z-index: 3;
}

.tag-center__athlete-selector-wrapper {
	padding-top: 24px;
	padding-bottom: 24px;
	border-bottom: 1px solid var(--color-grey-800);

	.is-mobile & {
		padding-top: 16px;
		padding-bottom: 16px;
	}
}

.tag-center__content-inner-w {
	overflow: hidden;
}

// event / team tabs in header
.tag-center__tabs::v-deep {
	.tabs li {
		padding: 16px 0;
		background: var(--color-grey-950);

		&.is-active {
			background: var(--color-grey-900);
			border-bottom: none;
		}

		.is-mobile & {
			background: var(--color-black);
			border-bottom: none;
			padding: 12px 0;

			&.is-active {
				background: var(--color-grey-950);
			}
		}
	}
}

.tag-center__goal-delete-picker-container {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
}

.tag-center__disable-overlay {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
	background: rgba(31, 36, 42, 0.86);
	backdrop-filter: blur(5px);
	width: 100%;
	height: 100%;
}

.tag-center__toast {
	padding: 12px 12px;
	position: absolute;
	top: 25%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 4;
	width: calc(100% - 24px);
	text-align: center;
	background: var(--color-grey-900);
	color: var(--color-white);

	&.warning {
		background: var(--color-red);
		color: var(--color-white);
	}
}

.tag-center__tabs-scrollable-content {
	overflow-y: auto;
	overflow-x: hidden;
}
</style>
